import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  AppointmentAuthorizationService,
  AppointmentModel,
  AuthorizationLikeItem,
  AvailityServiceReview,
  CrudService,
  FacilityService,
  filterAction,
  getPortalRuleAPIName,
  INSURANCE_PROVIDER_TRANSACTION_TYPE,
  InsuranceProviderConfiguration,
  InsuranceProviderLookupsService,
  Patient,
  PatientDataService,
  PatientInsurance,
  PortalRuleModel,
  ProductModel,
  ReferralModel,
  SelectOption,
  WebsocketAction,
  WebSocketService,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { filter as _filter, flatten, uniq } from 'lodash'
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  finalize,
  first,
  forkJoin,
  map,
  type Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs'

import type { ServiceReviewInquiryFormValue } from '../service-review-inquiry-form'

export interface ServiceReviewInquiryDialogData {
  patient: Patient
  type: 'referrals' | 'authorizations'
  appointment: AppointmentModel
  insurance?: PatientInsurance
  products?: ProductModel[]
}

export interface PullPriorAuthorizationPayload {
  requestTypeCode: InsuranceProviderConfiguration.SUBTYPE_ID
  fromDate: string
  toDate: string
  certificationIssueDate: string
  certificationNumber: string
  referenceNumber: string
  portalRuleIds?: string[]
}

export interface PullPriorAuthorizationResponse {
  action: string
  data: AvailityServiceReview.Review[]
  message: string
  topic: string
}

export type PullPriorAuthorizationMode = 'request' | 'results'

@Component({
  templateUrl: './pull-prior-authorization.component.html',
  styleUrls: ['./pull-prior-authorization.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ServiceReviewInquiryDialogComponent implements OnInit, OnDestroy {
  private readonly onDestroy$ = new Subject<void>()
  public isLoading$ = new BehaviorSubject<boolean>(false)
  public noSelection = false

  public readonly SUBTYPE = InsuranceProviderConfiguration.SUBTYPE_ID

  public loading$ = combineLatest([
    this.isLoading$,
    this.insuranceProviderLookupsService.loading$,
  ]).pipe(
    map(([isLoading, isLookupsLoading]) => isLoading || isLookupsLoading),
  )

  public mode$ = new BehaviorSubject<PullPriorAuthorizationMode>('request')

  public availableServiceTypes: InsuranceProviderConfiguration.SUBTYPE_ID[]
  public availableInsurances$: Observable<SelectOption<PatientInsurance>[]> = this.patientDataService.insurancePairs$.pipe(
    takeUntil(this.onDestroy$),
    map(pairs => pairs.map(([policy, lookup]) => {
      const canPull = lookup.supports(INSURANCE_PROVIDER_TRANSACTION_TYPE.SERVICE_REVIEWS_INQUIRY)
      let viewValue = policy.memberId ? `${lookup.viewValue} (${policy.memberId})` : lookup.viewValue

      if (!canPull) {
        viewValue += ' (278I Unsupported)'
      }

      const option: SelectOption<PatientInsurance> = {
        value: policy,
        viewValue,
        disabled: !canPull,
      }
      return option
    })),
  )

  public formGroup = this.fb.group({
    insurance: this.fb.control<PatientInsurance>(null, Validators.required),
    referrer: this.fb.control<string>(null, Validators.required),
    requestTypeCode: this.fb.control<InsuranceProviderConfiguration.SUBTYPE_ID>(null, Validators.required),
    portalRules: this.fb.control<PortalRuleModel[]>(null, Validators.required),
    priorAuthorization: this.fb.control(null, Validators.required),
    serviceReviews: this.fb.control<AuthorizationLikeItem[]>(null, [Validators.required, Validators.minLength(1)]),
  })

  public errorsVisible$ = new BehaviorSubject<boolean>(false)

  public portalRules$ = this.formGroup.get('insurance').valueChanges.pipe(
    startWith(this.formGroup.get('insurance').value),
    filter(() => !!this.formGroup.get('insurance').value?.carrierLookupId),
    switchMap(() => this.crudService.index(PortalRuleModel, { insuranceProviderLookupId: this.formGroup.get('insurance').value.carrierLookupId })),
    tap((portalRules) => {
      const appointmentPortalRules = this.data.appointment.portalRules || []
      const selectedPortalRules = portalRules.filter((r) => appointmentPortalRules.some((pr) => pr.id === r.id))

      this.formGroup.get('portalRules').setValue(selectedPortalRules)
    }),
  )

  public serviceReviews$: Observable<AuthorizationLikeItem[]> = this.websocketService.clinicianMessage$
    .pipe(
      filterAction(WebsocketAction.PRIOR_AUTHORIZATION_INDEX),
      catchError(() => {
        this.isLoading$.next(false)
        return of(null)
      }),
      filter((results) => ['success', 'partial'].includes(results.status)),
      map((results) => {
        const serviceReviewsByPortalRule = (results.data || []).filter((row: Record<string, any>) => row.success)
        this.isLoading$.next(false)
        if (!serviceReviewsByPortalRule?.length) {
          this.mode$.next('request')
          return []
        }

        this.mode$.next('results')

        return serviceReviewsByPortalRule.reduce((acc: AuthorizationLikeItem[], row: Record<string, any>) => {
          return [
            ...acc,
            ...row.serviceReviews,
          ]
        }, [])
      }),
      shareReplay(1),
    )

  public serviceReviewErrors$ = this.websocketService.clinicianMessage$
    .pipe(
      filterAction(WebsocketAction.PRIOR_AUTHORIZATION_INDEX),
      filter((results) => results.status !== 'success'),
      catchError(() => {
        this.isLoading$.next(false)
        return of(null)
      }),
      map((results) => {
        const responseRows: any[] = results.data || []

        if (responseRows.length === 0) {
          return [{ errorMessage: this.translateService.instant('dialog.pull-pre-auth.no-service-reviews-error') }]
        }

        return responseRows.map((row) => {
          return {
            api: row.api,
            errors: row.errors || [],
          }
        })
      }),
      tap((errors) => {
        this.isLoading$.next(false)
        if (errors.length > 0) {
          this.errorsVisible$.next(true)
        }
      }),
    )

  public resultsVisible$ = combineLatest([
    this.mode$,
    this.serviceReviews$,
  ])
    .pipe(
      map(([mode, serviceReviews]) => mode === 'results' && !!serviceReviews),
    )

  public procedureCodes: string[] = []

  public get defaultPortalRulesName() {
    return (this.data.appointment.portalRules || [])
      .map((rule) => this.translateService.instant(getPortalRuleAPIName(rule.api)))
      .join(', ')
  }

  constructor(
    public dialogRef: MatDialogRef<ServiceReviewInquiryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ServiceReviewInquiryDialogData,
    private readonly patientDataService: PatientDataService,
    private readonly websocketService: WebSocketService,
    private readonly fb: FormBuilder,
    private readonly authorizationService: AppointmentAuthorizationService,
    private readonly crudService: CrudService,
    private readonly snackbar: MatSnackBar,
    private readonly facilityService: FacilityService,
    private readonly insuranceProviderLookupsService: InsuranceProviderLookupsService,
    private readonly translateService: TranslateService,
  ) { }

  public ngOnInit(): void {
    this.availableServiceTypes = this.data.type === 'referrals'
      ? [InsuranceProviderConfiguration.SUBTYPE_ID.REFERRALS]
      : [InsuranceProviderConfiguration.SUBTYPE_ID.HEALTH_SERVICES, InsuranceProviderConfiguration.SUBTYPE_ID.ADMISSION]
    const defaultServiceReviewType = this.availableServiceTypes[0]

    this.formGroup.patchValue({
      requestTypeCode: defaultServiceReviewType,
      referrer: this.data.appointment.referrer?.id ?? this.data.appointment.referrerId ?? null,
    })

    this.procedureCodes = _filter(uniq(flatten(this.data.appointment.products.map(product => product.codes))))

    if (this.data.insurance) {
      this.formGroup.controls.insurance.setValue(this.data.insurance)
    } else {
      this.availableInsurances$.pipe(
        takeUntil(this.onDestroy$),
        filter(insuranceOptions => insuranceOptions.length > 0),
        first(),
      ).subscribe(insuranceOptions => {
        const insurance = insuranceOptions[0].value
        this.formGroup.controls.insurance.setValue(insurance)
      })
    }
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public onSubmit(mode: PullPriorAuthorizationMode) {
    this.errorsVisible$.next(false)
    switch (mode) {
      case 'request':
        this.isLoading$.next(true)
        this.websocketService.messageQueue.next({
          action: WebsocketAction.PRIOR_AUTHORIZATION_INDEX,
          appointmentId: this.data.appointment.id,
          patientId: this.data.patient.id,
          referrerId: this.formGroup.value.referrer,
          insuranceId: this.formGroup.value.insurance.id,
          providerLookupId: this.formGroup.value.insurance.carrierLookupId,
          facilityId: this.facilityService.current$.getValue().id,
          data: this.getPayload(),
        })
        break
      case 'results':
        if (this.formGroup.value.serviceReviews?.length > 0) {
          this.saveSelectedServiceReviews()
        } else {
          this.noSelection = true
        }
        break
    }
  }

  public comparePortalRules(a: PortalRuleModel, b: PortalRuleModel): boolean {
    return a?.id === b?.id
  }

  private getPayload(): PullPriorAuthorizationPayload {
    const { requestTypeCode, portalRules } = this.formGroup.value
    const inquiryFormValue = (this.formGroup.value.priorAuthorization ?? {}) as ServiceReviewInquiryFormValue

    return {
      requestTypeCode: requestTypeCode ?? InsuranceProviderConfiguration.SUBTYPE_ID.HEALTH_SERVICES,
      fromDate: inquiryFormValue?.fromDate?.format('YYYY-MM-DD'),
      toDate: inquiryFormValue?.toDate?.format('YYYY-MM-DD'),
      certificationIssueDate: inquiryFormValue?.certificationIssueDate?.format('YYYY-MM-DD'),
      certificationNumber: inquiryFormValue?.certificationNumber,
      referenceNumber: inquiryFormValue?.referenceNumber,
      portalRuleIds: portalRules?.map((rule) => rule.id) ?? undefined,
    }
  }

  private saveSelectedServiceReviews() {
    this.isLoading$.next(true)

    if (this.data.type === 'referrals') {
      this.saveReferrals()
    } else {
      this.saveAuthorizations()
    }
  }

  private saveAuthorizations() {
    const patientInsurance = this.formGroup.value.insurance

    this.authorizationService.bulkCreateAuthorizations(
      this.data.appointment.id,
      patientInsurance.carrierLookupId,
      patientInsurance.id,
      this.formGroup.value.serviceReviews,
    )
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.isLoading$.next(false)),
        catchError((e) => {
          this.snackbar.open(e.error?.message ?? e.message, '', { duration: 5000 })

          return of(null)
        }),
      )
      .subscribe((authorizationList) => {
        if (authorizationList) {
          this.dialogRef.close(authorizationList)
        }
      })
  }

  private saveReferrals() {
    const patientInsurance = this.formGroup.value.insurance
    const serviceReviews = this.formGroup.value.serviceReviews

    const obs = serviceReviews.map(serviceReview => {
      const referral = new ReferralModel({
        patientId: this.data.patient.id,
        appointmentIds: [this.data.appointment.id],
        insuranceId: patientInsurance.id,
        carrierLookupId: patientInsurance.carrierLookupId,
        carrierId: patientInsurance.carrierId,
        number: serviceReview.certificationNumber,
      })
      return this.crudService.create(referral)
    })

    forkJoin(obs).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.isLoading$.next(false)),
      catchError((e) => {
        this.snackbar.open(e.error?.message ?? e.message, '', { duration: 5000 })
        return of(null)
      }),
    ).subscribe((referrals) => {
      if (referrals) {
        this.dialogRef.close(referrals)
      }
    })
  }
}
